import React, {
  useContext,
  useState,
  useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import useEnableExhibitorPanelTreatment from 'hooks/useEnableExhibitorPanelTreatment';
import { UserSessionContext } from 'components/UserSession';
import { isTwilioFullscreen, selectChatInterfaceState } from 'models/twilio';
import { selectEnrichmentDataShow } from 'models/enrichmentData';
import isChatroomDisabled from 'components/chat/utils/isChatroomDisabled';
import { POSITIONING } from 'components/twilio/constants';

import ExhibitorHeader from './ExhibitorHeader';
import UserActivityLog from './UserActivityLog';

interface ExhibitorPanelContainerProps {
  $isOpened: boolean;
  $chatActive: boolean;
  $twilioFullscreen: boolean;
  $showEnrichmentData: boolean;
  $chatDisabled: boolean;
}

const ExhibitorPanelContainer = styled.div<ExhibitorPanelContainerProps>`
  width: 760px;
  height: ${({ $isOpened }) => ($isOpened ? 300 : 48)}px;
  max-height: ${({ $chatActive }) => ($chatActive
    ? `calc(100vh - ${POSITIONING.DESKTOP.INTERFACE_HEIGHT} - 64px)`
    : `calc(100vh - ${POSITIONING.HEADER_HEIGHT} - 64px)`)};
  position: fixed;
  bottom: ${({ $chatActive, $chatDisabled }) => {
    if ($chatDisabled) return 0;
    return ($chatActive ? 550 : 48);
  }}px;
  right: ${({ $showEnrichmentData }) => ($showEnrichmentData ? 315 : 20)}px;
  border-radius: 4px;
  background-color: white;
  z-index: 1000;
  transition: height 0.4s, bottom 0.5s, right 0.4s;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.16);

  ${({ $twilioFullscreen, $chatActive }) => $twilioFullscreen && $chatActive && 'bottom: 100%'}
`;

const ExhibitorPanelContent = styled.div<{ isOpened: boolean }>`
  width: 100%;
  height: calc(100% - 48px);
  display: flex;
  transition: all 0.4s;
  opacity: ${({ isOpened }) => (isOpened ? 1 : 0)};
`;

const ExhibitorPanel = () => {
  const [isOpened, setIsOpened] = useState(true);
  const { chatroomConfig } = useSelector((s) => s.eventConfigs);
  const twilioActive = useSelector(selectChatInterfaceState);
  const twilioFullscreen = useSelector(isTwilioFullscreen);

  const {
    customAttendeeFields: { vexpo_exhibitor_booth_id: vexpoExhibitorBoothId = '' } = {},
    authToken,
  } = useContext(UserSessionContext).userSession || {};

  const showExhibitorPanel = useEnableExhibitorPanelTreatment();

  const showEnrichmentData = useSelector(selectEnrichmentDataShow);

  const isChatDisabled = useMemo(() => isChatroomDisabled(chatroomConfig), [chatroomConfig]);

  if (!showExhibitorPanel) {
    return null;
  }

  return (
    <ExhibitorPanelContainer
      $isOpened={isOpened}
      $showEnrichmentData={showEnrichmentData}
      $chatActive={twilioActive}
      $twilioFullscreen={twilioFullscreen}
      $chatDisabled={isChatDisabled}
    >
      <ExhibitorHeader
        isOpened={isOpened}
        setIsOpened={setIsOpened}
      />
      <ExhibitorPanelContent
        isOpened={isOpened}
      >
        <UserActivityLog
          boothId={vexpoExhibitorBoothId}
          currentUserAuthToken={authToken}
          isOpened={isOpened}
        />
      </ExhibitorPanelContent>
    </ExhibitorPanelContainer>
  );
};

export default ExhibitorPanel;
